/* eslint-disable no-param-reassign */
/* eslint-disable camelcase */
import Cookies from 'js-cookie';
import {createApi, fetchBaseQuery} from '@reduxjs/toolkit/query/react';
import {StoreSettings} from 'models/storeSettings';
import {
  Product,
  ProductFromJSON,
  ConfirmOrder,
  ConfirmOrderFromJSON,
  CampaignConfirmation,
  CampaignConfirmationFromJSON
} from '@customink/stores-api-client';
import {CampaignContactDetails} from 'models/campaignContactDetails';
import {UserAccount} from 'models/userAccount';
import {Location, StoreAddress} from 'models/storeAddress';
import {Country} from 'adapters/geography/country';
import {AddressValidation} from 'models/addressValidation';
import {OrderSummary} from '../models/orderSummary';
import {OrderConfirmationResponse} from '../models/orderConfirmation';
import {
  StoreDetails,
  StoreUpdateParamsType,
  StoreFulfillViaType,
  CampaignAttributes,
  StoreCapabilities
} from '../models/storeDetails';
import {StoreProduct} from '../models/storeProduct';
import {CartDetail, AddToCartParams} from '../models/cart';
import services from './remoteServices';
import storesSerializer from '../serializers/stores';
import settingsSerializer from '../serializers/settings';
import {
  orderSummaryTransformer,
  orderConfirmationTransformer,
  purchaseDetailTransformer,
  storeProductsTransformer,
  settingsTransformer,
  storesDeserializer
} from '../deserializers/stores';
import {addErrorSubscriber} from '../middleware/errorMiddleware';
import {setError} from '../slices/appSlice';
import {convertStringToFloat, toCamelCase} from '../utils/helpers';
import {StorePurchase} from '../models/storePurchase';

export type StoreStatus = 'open' | 'draft' | 'closed';

export type StoreParamsType = {
  name?: string;
  url_slug?: string;
  status?: StoreStatus;
  logo?: string;
  hero_image?: string;
  header_logo_type?: string;
  hero_logo_visibility?: string;
  header_background_color?: string;
  banner_enabled?: boolean;
  banner_text?: string;
};

export type StoreCreateParamsType = {
  name?: string;
  url_slug: string;
  status?: StoreStatus;
};

export type Store = {
  id: number;
  name: string;
  url_slug: string;
  description: string | null;
  company_url: string | null;
  created_at: string;
  updated_at: string;
  creator_id: number | null;
  rep_id: number | null;
  contact_information: string;
  status: StoreStatus;
};

export type StoreProductVariant = {
  color_id: number;
};

export type StoreProductParamsType = {
  url_slug: string;
  product: {
    name: string;
    style_id: number;
    design_composite_id: string;
    enabled: boolean;
    variants: StoreProductVariant[];
  };
};

export type StoreProductUpdateParamsType = {
  url_slug: string;
  product_id: number;
  product: {
    enabled: boolean;
  };
};

export type StoreProductQueryParamType = {
  urlSlug: string;
  productId: number;
  displayImagesOnly?: boolean;
};

export type StoreProductsQueryParamType = {
  urlSlug: string;
  allProducts: boolean;
};

export type StoreQueryDetails = {
  name: string;
  url_slug: string;
  status: StoreStatus;
  logo_image_url: string;
  hero_image_url: string;
  banner_color: string;
  text_color: string;
  header_transparent: boolean;
  header_logo_type: 'text' | 'logo' | 'custom_ink_branding';
  hero_logo_visibility:
    | 'hidden'
    | 'hero_logo'
    | 'hero_background'
    | 'hero_logo_and_background';
  banner_enabled: boolean;
  banner_text: string;
  store_owner_email: string;
  description: string;
  company_url: string;
  event_store: boolean;
  campaigns: CampaignAttributes;
  capabilities: StoreCapabilities;
  fulfill_via: StoreFulfillViaType;
};

export type StoreProductReorderParamsType = {
  url_slug: string;
  product_ids: number[];
};

export type StorePurchaseUpdateParamsType = {
  urlSlug: string;
  referenceId: string;
  email: string;
};

export type StorePurchaseCancelParamsType = {
  url_slug: string;
  reference_id: string;
};

export type CampaignPurchaseParamsType = {
  url_slug: string;
  id: number;
};

export type GetPriceParamsType = {
  productId: string;
  frontColorCount?: number;
  backColorCount?: number;
};

export type StoreAnalyticsDataType = {
  totals: StoresAnalyticsTotalsType;
  previousTotals: StoresAnalyticsTotalsType;
  sales: StoresAnalyticsSalesType[];
  traffic: StoresAnalyticsTrafficType[];
};

export type StoresAnalyticsTotalsType = {
  itemsTotal: number;
  netSales: number;
  refundTotal: number;
  salesQty: number;
  salesTotal: number;
  voucherQty: number;
  voucherTotal: number;
  profits?: number;
};

export type StoresAnalyticsSalesType = {
  date: string;
  items: number;
  netSales: number;
  refunds: number;
  sales: number;
  vouchers: number;
};

export type StoresAnalyticsTrafficType = {
  date: string;
  pageviews: number;
};

export type StoreAnalyticsProductDataType = {
  currentPage?: number;
  totalPages?: number;
  products: Array<StoreAnalyticsProductType>;
};

export type StoreAnalyticsProductType = {
  productId: number;
  image: string;
  name: string;
  price: number;
  quantity: number;
  sales: number;
  margin?: number;
  profit?: number;
  daysInStore: number;
};

export type StoreAnalyticsRTKType = {
  urlSlug: string;
  queryParams?: StoreAnalyticsQueryParamsType;
};

export type StoreAnalyticsQueryParamsType = {
  date_start: string;
  date_end: string;
  page?: number;
  bucket_size: BucketSize;
};

export type GetOrderSummaryParam = {
  urlSlug: string;
  campaignId: number | string;
};

export type GetOrderConfirmationParam = {
  storeSlug: string;
  campaignId: string;
};

export type GroupOrderCancelParamsType = {
  url_slug: string;
  id: number | string;
};

export type ReplaceCampaignParamsType = {
  storeSlug: string;
  campaignId: number;
};

export type ReopenCampaignParamsType = {
  storeSlug: string;
  campaignId: number;
};

export type GetBuyupParam = {
  urlSlug: string;
  campaignId: number | string;
};

export type GetCampaignContactDetailsParamsType = {
  storeSlug: string;
};

export type UpdateCampaignContactDetailsParamsType = {
  storeSlug: string;
} & CampaignContactDetails;

export type GetUserAccountParamsType = {
  email: string;
};

export type GetStoreAddressesParamsType = {
  storeSlug: string;
};

export type GetShippableCountriesParamsType = {
  storeSlug: string;
};

export type UpdateStoreAddressParamsType = {
  storeSlug: string;
  addressId: number;
  shippingFirstName: string;
  shippingLastName: string;
  shippingAddressOne: string;
  shippingAddressTwo: string;
  shippingCity: string;
  shippingState: string;
  shippingCountry: string;
  shippingPhoneNumber: string;
  shippingPostalCode: string;
};

export type ValidateAddressParamsType = {
  storeSlug: string;
  shippingFirstName: string;
  shippingLastName: string;
  shippingAddressOne: string;
  shippingAddressTwo: string;
  shippingCity: string;
  shippingState: string;
  shippingCountry: string;
  shippingPhoneNumber: string;
  shippingPostalCode: string;
};

export type CreateStoreAddressParamsType = {
  storeSlug: string;
  shippingFirstName: string;
  shippingLastName: string;
  shippingAddressOne: string;
  shippingAddressTwo: string;
  shippingCity: string;
  shippingState: string;
  shippingCountry: string;
  shippingPhoneNumber: string;
  shippingPostalCode: string;
};

export type BucketSize = 'day' | 'week';
export const storesApi = createApi({
  reducerPath: 'storeApi',
  baseQuery: fetchBaseQuery({
    baseUrl: `${services.stores}/api`,
    credentials: 'include'
  }),
  tagTypes: [
    'Store',
    'StoreProduct',
    'Cart',
    'Purchases',
    'Order',
    'Settings',
    'ContactDetails',
    'UserAccount',
    'StoreAddresses',
    'Countries'
  ],
  endpoints: builder => ({
    createStore: builder.mutation({
      query: (params: StoreParamsType) => ({
        url: '/stores/',
        method: 'POST',
        body: {store: {...params, created_via: 'self_serve'}},
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': decodeURIComponent(Cookies.get('stores_csrf'))
        }
      }),
      invalidatesTags: ['Store']
    }),
    getStoreDetails: builder.query<StoreDetails, string>({
      query: (urlSlug: string) => ({
        url: `/stores/${urlSlug}`,
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': decodeURIComponent(Cookies.get('stores_csrf'))
        }
      }),
      transformResponse: storesDeserializer,
      providesTags: ['Store']
    }),
    getStoreSettings: builder.query<StoreSettings, string>({
      query: (urlSlug: string) => ({
        url: `/stores/${urlSlug}/settings`,
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': decodeURIComponent(Cookies.get('stores_csrf'))
        }
      }),
      transformResponse: settingsTransformer,
      providesTags: ['Settings']
    }),
    createStoreProduct: builder.mutation<void, StoreProductParamsType>({
      query: params => ({
        url: `/stores/${params.url_slug}/products`,
        method: 'POST',
        body: params.product,
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': decodeURIComponent(Cookies.get('stores_csrf'))
        }
      }),
      invalidatesTags: ['StoreProduct']
    }),
    getStoreAnalyticsData: builder.query<
      StoreAnalyticsDataType,
      StoreAnalyticsRTKType
    >({
      query: params => {
        const {urlSlug, queryParams} = params;
        return {
          url: `/stores/${urlSlug}/analytics/data`,
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': decodeURIComponent(Cookies.get('stores_csrf'))
          },
          params: queryParams
        };
      }
    }),
    getStoreAnalyticsProducts: builder.query<
      StoreAnalyticsProductDataType,
      StoreAnalyticsRTKType
    >({
      query: params => {
        const {urlSlug, queryParams} = params;
        return {
          url: `/stores/${urlSlug}/analytics/products`,
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': decodeURIComponent(Cookies.get('stores_csrf'))
          },
          params: queryParams
        };
      },
      transformResponse: (response: StoreAnalyticsProductDataType) => {
        response.products.forEach(product => {
          if (Object.prototype.hasOwnProperty.call(product, 'margin')) {
            product.margin = convertStringToFloat(product.margin);
            product.profit = convertStringToFloat(product.profit);
          }
          product.price = convertStringToFloat(product.price);
        });
        return response;
      },
      providesTags: ['Store']
    }),
    getStoreProducts: builder.query<
      StoreProduct[],
      StoreProductsQueryParamType | string
    >({
      query: (params: StoreProductsQueryParamType | string) => {
        let urlSlug;
        let allProducts;
        if (typeof params === 'string') {
          urlSlug = params;
          allProducts = false;
        } else {
          urlSlug = params.urlSlug;
          allProducts = params.allProducts;
        }
        const url = `/stores/${urlSlug}/products${
          allProducts ? '?all_products=true' : ''
        }`;
        return {
          url,
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': decodeURIComponent(Cookies.get('stores_csrf'))
          }
        };
      },
      providesTags: ['StoreProduct'],
      transformResponse: storeProductsTransformer
    }),
    getStoreProduct: builder.query<Product, StoreProductQueryParamType>({
      query: params => ({
        url: `/stores/${params.urlSlug}/products/${params.productId}${
          params.displayImagesOnly === true ? '?display_images_only=true' : ''
        }`,
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': decodeURIComponent(Cookies.get('stores_csrf'))
        }
      }),
      providesTags: ['StoreProduct'],
      transformResponse: ProductFromJSON
    }),
    updateStoreProduct: builder.mutation<void, StoreProductUpdateParamsType>({
      query: params => ({
        url: `/stores/${params.url_slug}/products/${params.product_id}`,
        method: 'PATCH',
        body: params.product,
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': decodeURIComponent(Cookies.get('stores_csrf'))
        }
      }),
      invalidatesTags: ['StoreProduct']
    }),
    updateStore: builder.mutation<
      void,
      {details: StoreUpdateParamsType; slug: string}
    >({
      query: ({
        details,
        slug
      }: {
        details: StoreUpdateParamsType;
        slug: string;
      }) => {
        const mutationParams = storesSerializer({...details});

        return {
          url: `/stores/${slug}`,
          method: 'PATCH',
          body: {store: mutationParams},
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': decodeURIComponent(Cookies.get('stores_csrf'))
          }
        };
      },
      invalidatesTags: ['Store']
    }),
    updateSettings: builder.mutation<
      void,
      {
        details: StoreSettings;
        slug: string;
      }
    >({
      query: ({details, slug}: {details: StoreSettings; slug: string}) => {
        const mutationParams = settingsSerializer({...details});

        return {
          url: `/stores/${slug}/settings`,
          method: 'PATCH',
          body: {...mutationParams},
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': decodeURIComponent(Cookies.get('stores_csrf'))
          }
        };
      },
      invalidatesTags: ['Store', 'Settings']
    }),
    reorderStoreProducts: builder.mutation<void, StoreProductReorderParamsType>(
      {
        query: params => ({
          url: `/stores/${params.url_slug}/products/reorder`,
          method: 'PATCH',
          body: {product_ids: params.product_ids},
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': decodeURIComponent(Cookies.get('stores_csrf'))
          }
        }),
        invalidatesTags: ['StoreProduct']
      }
    ),
    getPrice: builder.query<string, GetPriceParamsType>({
      query: params =>
        `/price?product_id=${params.productId}${
          params.frontColorCount
            ? `&color_counts[front]=${params.frontColorCount}`
            : ''
        }${
          params.backColorCount
            ? `&color_counts[back]=${params.backColorCount}`
            : ''
        }`,
      transformResponse: (response: {price: string}) => {
        return response.price;
      }
    }),
    getCart: builder.query<CartDetail, string>({
      query: (urlSlug: string) => ({
        url: `/stores/${urlSlug}/cart`,
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Content-Type': 'application/json',
          'X-CSRF-Token': decodeURIComponent(Cookies.get('stores_csrf'))
        }
      }),
      providesTags: ['Cart']
    }),
    addToCart: builder.mutation<void, AddToCartParams>({
      query: params => ({
        url: `/stores/${params.urlSlug}/cart/add`,
        method: 'POST',
        body: params,
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': decodeURIComponent(Cookies.get('stores_csrf'))
        }
      }),
      invalidatesTags: ['Cart']
    }),
    getPurchases: builder.query<StorePurchase, string>({
      query: (urlSlug: string) => ({
        url: `/stores/${urlSlug}/purchases`,
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': decodeURIComponent(Cookies.get('stores_csrf'))
        }
      }),
      providesTags: ['Purchases'],
      transformResponse: purchaseDetailTransformer
    }),
    cancelPurchase: builder.mutation<void, StorePurchaseCancelParamsType>({
      query: params => ({
        url: `/stores/${params.url_slug}/purchases/cancel`,
        method: 'PATCH',
        body: {id: params.reference_id},
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': decodeURIComponent(Cookies.get('stores_csrf'))
        }
      }),
      invalidatesTags: ['Purchases']
    }),
    updatePurchase: builder.mutation<void, StorePurchaseUpdateParamsType>({
      query: params => ({
        url: `/stores/${params.urlSlug}/purchases/update`,
        method: 'PATCH',
        body: {id: params.referenceId, email: params.email},
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': decodeURIComponent(Cookies.get('stores_csrf'))
        }
      }),
      invalidatesTags: ['Purchases']
    }),
    campaignPurchase: builder.mutation<void, CampaignPurchaseParamsType>({
      query: params => ({
        url: `/stores/${params.url_slug}/campaigns/${params.id}/fulfill`,
        method: 'PATCH',
        body: {id: params.id},
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': decodeURIComponent(Cookies.get('stores_csrf'))
        }
      }),
      invalidatesTags: ['Order']
    }),
    getOrderSummary: builder.query<OrderSummary, GetOrderSummaryParam>({
      query: params => ({
        url: `/stores/${params.urlSlug}/campaigns/${params.campaignId}`,
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': decodeURIComponent(Cookies.get('stores_csrf'))
        }
      }),
      providesTags: ['Order'],
      transformResponse: orderSummaryTransformer
    }),
    getBuyup: builder.query<string, GetBuyupParam>({
      query: params => ({
        url: `/stores/${params.urlSlug}/campaigns/${params.campaignId}/buyup`,
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': decodeURIComponent(Cookies.get('stores_csrf'))
        }
      }),
      providesTags: ['Order', 'Store'],
      transformResponse: (response: {url: string}) => {
        return response.url;
      }
    }),
    getOrderConfirmation: builder.query<
      OrderConfirmationResponse,
      GetOrderConfirmationParam
    >({
      query: params => ({
        url: `/stores/${params.storeSlug}/campaigns/confirm_order/${params.campaignId}`,
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': decodeURIComponent(Cookies.get('stores_csrf'))
        }
      }),
      transformResponse: orderConfirmationTransformer
    }),
    getHybridOrderConfirmation: builder.query<
      ConfirmOrder,
      GetOrderConfirmationParam
    >({
      query: params => ({
        url: `/stores/${params.storeSlug}/campaigns/confirm_order/${params.campaignId}`,
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': decodeURIComponent(Cookies.get('stores_csrf'))
        }
      }),
      transformResponse: ConfirmOrderFromJSON
    }),
    getHybridOrderConfirmationNew: builder.query<
      CampaignConfirmation,
      GetOrderConfirmationParam
    >({
      query: params => ({
        url: `/stores/${params.storeSlug}/campaigns/confirm/${params.campaignId}`,
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': decodeURIComponent(Cookies.get('stores_csrf'))
        }
      }),
      transformResponse: CampaignConfirmationFromJSON
    }),
    cancelGroupOrder: builder.mutation<void, GroupOrderCancelParamsType>({
      query: params => ({
        url: `/stores/${params.url_slug}/campaigns/${params.id}/cancel`,
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': decodeURIComponent(Cookies.get('stores_csrf'))
        }
      }),
      invalidatesTags: [
        'Store',
        'StoreProduct',
        'Purchases',
        'Settings',
        'Order'
      ]
    }),
    replaceCampaign: builder.mutation<void, ReplaceCampaignParamsType>({
      query: params => ({
        url: `/stores/${params.storeSlug}/campaigns/${params.campaignId}/replace`,
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': decodeURIComponent(Cookies.get('stores_csrf'))
        }
      }),
      invalidatesTags: ['Store', 'StoreProduct']
    }),
    reopenCampaign: builder.mutation<void, ReopenCampaignParamsType>({
      query: params => ({
        url: `/stores/${params.storeSlug}/campaigns/${params.campaignId}/reopen`,
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': decodeURIComponent(Cookies.get('stores_csrf'))
        }
      }),
      invalidatesTags: ['Store', 'StoreProduct', 'Settings']
    }),
    getContactDetails: builder.query<
      CampaignContactDetails,
      GetCampaignContactDetailsParamsType
    >({
      query: params => ({
        url: `/stores/${params.storeSlug}/contact_details`,
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': decodeURIComponent(Cookies.get('stores_csrf'))
        }
      }),
      providesTags: ['ContactDetails'],
      transformResponse: (response: any) => {
        return {
          organizerFirstName: response.organizer_first_name,
          organizerLastName: response.organizer_last_name,
          organizerPhoneNumber: response.organizer_phone_number,
          organizerEmail: response.organizer_email
        };
      }
    }),
    updateContactDetails: builder.mutation<
      CampaignContactDetails,
      UpdateCampaignContactDetailsParamsType
    >({
      query: params => ({
        url: `/stores/${params.storeSlug}/contact_details`,
        method: 'PATCH',
        body: {
          organizer_first_name: params.organizerFirstName,
          organizer_last_name: params.organizerLastName,
          organizer_phone_number: params.organizerPhoneNumber,
          organizer_email: params.organizerEmail
        },
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': decodeURIComponent(Cookies.get('stores_csrf'))
        }
      }),
      invalidatesTags: ['ContactDetails', 'Store']
    }),
    getUserAccount: builder.query<UserAccount, GetUserAccountParamsType>({
      query: params => ({
        url: `/users/account?email=${params.email}`,
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': decodeURIComponent(Cookies.get('stores_csrf'))
        }
      }),
      providesTags: ['UserAccount']
    }),
    getStoreAddresses: builder.query<
      StoreAddress[],
      GetStoreAddressesParamsType
    >({
      query: params => ({
        url: `/stores/${params.storeSlug}/addresses`,
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': decodeURIComponent(Cookies.get('stores_csrf'))
        }
      }),
      transformResponse: (response: StoreAddress[]) =>
        response.map(address => {
          return {
            ...toCamelCase<StoreAddress>(address),
            location: toCamelCase<Location>(address.location)
          };
        }),
      providesTags: ['StoreAddresses']
    }),
    updateStoreAddress: builder.mutation<
      StoreAddress,
      UpdateStoreAddressParamsType
    >({
      query: params => ({
        url: `/stores/${params.storeSlug}/addresses/${params.addressId}`,
        method: 'PATCH',
        body: {
          first_name: params.shippingFirstName,
          last_name: params.shippingLastName,
          address_1: params.shippingAddressOne,
          address_2: params.shippingAddressTwo,
          city: params.shippingCity,
          state: params.shippingState,
          country: params.shippingCountry,
          phone_number: params.shippingPhoneNumber,
          postal_code: params.shippingPostalCode
        },
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': decodeURIComponent(Cookies.get('stores_csrf'))
        }
      }),
      invalidatesTags: ['StoreAddresses']
    }),
    validateAddress: builder.mutation<
      AddressValidation,
      ValidateAddressParamsType
    >({
      query: params => ({
        url: `/stores/${params.storeSlug}/addresses/validate`,
        method: 'POST',
        body: {
          first_name: params.shippingFirstName,
          last_name: params.shippingLastName,
          address_1: params.shippingAddressOne,
          address_2: params.shippingAddressTwo,
          city: params.shippingCity,
          state: params.shippingState,
          country: params.shippingCountry,
          phone_number: params.shippingPhoneNumber,
          postal_code: params.shippingPostalCode
        },
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': decodeURIComponent(Cookies.get('stores_csrf'))
        }
      })
    }),
    createStoreAddress: builder.mutation<
      StoreAddress,
      CreateStoreAddressParamsType
    >({
      query: params => ({
        url: `/stores/${params.storeSlug}/addresses`,
        method: 'POST',
        body: {
          first_name: params.shippingFirstName,
          last_name: params.shippingLastName,
          address_1: params.shippingAddressOne,
          address_2: params.shippingAddressTwo,
          city: params.shippingCity,
          state: params.shippingState,
          country: params.shippingCountry,
          phone_number: params.shippingPhoneNumber,
          postal_code: params.shippingPostalCode
        },
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': decodeURIComponent(Cookies.get('stores_csrf'))
        }
      }),
      invalidatesTags: ['StoreAddresses']
    }),
    getShippableCountries: builder.query<
      Country[],
      GetShippableCountriesParamsType
    >({
      query: params => ({
        url: `/stores/${params.storeSlug}/addresses/countries`,
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': decodeURIComponent(Cookies.get('stores_csrf'))
        }
      }),
      providesTags: ['Countries']
    })
  })
});

export const {
  useGetStoreDetailsQuery,
  useLazyGetStoreDetailsQuery,
  useCreateStoreMutation,
  useCreateStoreProductMutation,
  useUpdateStoreProductMutation,
  useGetStoreProductsQuery,
  useGetStoreProductQuery,
  useLazyGetStoreProductQuery,
  useReorderStoreProductsMutation,
  useUpdateStoreMutation,
  useUpdateSettingsMutation,
  useGetPriceQuery,
  useGetCartQuery,
  useLazyGetCartQuery,
  useAddToCartMutation,
  useGetStoreAnalyticsDataQuery,
  useGetStoreAnalyticsProductsQuery,
  useGetPurchasesQuery,
  useUpdatePurchaseMutation,
  useCancelPurchaseMutation,
  useGetOrderSummaryQuery,
  useLazyGetOrderSummaryQuery,
  useGetBuyupQuery,
  useLazyGetBuyupQuery,
  useGetOrderConfirmationQuery,
  useCampaignPurchaseMutation,
  useGetStoreSettingsQuery,
  useCancelGroupOrderMutation,
  useGetHybridOrderConfirmationNewQuery,
  useReplaceCampaignMutation,
  useReopenCampaignMutation,
  useGetContactDetailsQuery,
  useUpdateContactDetailsMutation,
  useLazyGetUserAccountQuery,
  useGetStoreAddressesQuery,
  useUpdateStoreAddressMutation,
  useValidateAddressMutation,
  useCreateStoreAddressMutation,
  useGetShippableCountriesQuery
} = storesApi;

addErrorSubscriber({
  matcher: action => {
    return (
      (action.meta?.arg?.endpointName === 'getStoreProducts' ||
        action.meta?.arg?.endpointName === 'getStoreProduct') &&
      action.payload?.status === 404
    );
  },
  callback: (action, api) => {
    api.dispatch(
      setError({
        displayType: 'notFound'
      })
    );
  }
});

addErrorSubscriber({
  matcher: action => {
    return (
      action.meta?.arg?.endpointName === 'getStoreDetails' &&
      (action.payload?.status === 403 || action.payload?.status === 404)
    );
  },
  callback: (action, api) => {
    api.dispatch(
      setError({
        displayType: 'notFound'
      })
    );
  }
});

addErrorSubscriber({
  matcher: action => {
    return (
      action.meta?.arg?.endpointName === 'cancelPurchase' &&
      action.payload?.status === 422
    );
  },
  callback: (action, api) => {
    api.dispatch(
      setError({
        displayType: 'alert',
        message: 'The order cannot be canceled.'
      })
    );
  }
});
